/* eslint-disable react/prop-types */
import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Card, CardContent, Grid, Icon, IconButton, Tooltip } from '@mui/material'
import { alpha } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { RichTreeView } from '@mui/x-tree-view/RichTreeView'
import { useTreeItem } from '@mui/x-tree-view/useTreeItem'
import {
    TreeItemContent,
    TreeItemGroupTransition,
    TreeItemIconContainer,
    TreeItemLabel,
    TreeItemRoot,
} from '@mui/x-tree-view/TreeItem'
import { TreeItemIcon } from '@mui/x-tree-view/TreeItemIcon'
import { TreeItemProvider } from '@mui/x-tree-view/TreeItemProvider'
import { useTreeItemModel } from '@mui/x-tree-view/hooks'
import { isUndefined } from 'lodash'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import TvIcon from '@mui/icons-material/Tv'
import AdsClickIcon from '@mui/icons-material/AdsClick'
import HelpIcon from '@mui/icons-material/Help'
import WidgetsIcon from '@mui/icons-material/Widgets'
import DescriptionIcon from '@mui/icons-material/Description'

const Action = ({
    onClick = () => {},
    icon,
    color = '#161832',
}) => {
    return (
        <IconButton
            sx={{ fontSize: '24px', padding: '3px' }}
            onClick={e => {
                e.stopPropagation()
                onClick()
            }}
        >
            <Icon fontSize='inherit' sx={{ color }}>{icon}</Icon>
        </IconButton>
    )
}

Action.propTypes = {
    onClick: PropTypes.func,
    icon: PropTypes.string,
    color: PropTypes.string,
}

const getIconTypeFeature = typeFeature => {
    switch (typeFeature) {
        case 'MODULE': return <WidgetsIcon />
        case 'ECRAN': return <TvIcon />
        case 'ACTION': return <AdsClickIcon />
        default: return <HelpIcon />
    }
}

const CustomLabel = ({ item, actions, children, ...other }) => (
    <TreeItemLabel {...other}>
        <Grid container spacing={1} alignItems='center'>
            <Grid size='auto'>
                {getIconTypeFeature(item.typeFeature)}
            </Grid>
            {item.pluggedInFrontend && (
                <Grid size='auto'>
                    <Tooltip title={'Branché sur l\'application'}>
                        <CheckCircleIcon sx={{ color: '#69cf69' }} />
                    </Tooltip>
                </Grid>
            )}
            <Grid size='auto'>
                <Typography sx={{ fontSize: '1rem' }}>{children}</Typography>
            </Grid>
            {item.description && (
                <Grid size='auto'>
                    <Tooltip title={item.description}>
                        <DescriptionIcon />
                    </Tooltip>
                </Grid>
            )}
            <Grid size='grow' />
            {actions.map(({ icon, onClick }, i) => (
                <Grid key={i} size='auto'>
                    <Action
                        icon={icon}
                        onClick={() => onClick(item.id)}
                    />
                </Grid>
            ))}
        </Grid>
    </TreeItemLabel>
)

const CustomTreeItem = React.forwardRef((props, ref) => {
    const {
        id, itemId, label, disabled, children, // for useTreeItem
        actions, // for getLabelProps
        ...other // for getRootProps
    } = props

    const {
        getContextProviderProps,
        getRootProps,
        getContentProps,
        getIconContainerProps,
        getLabelProps,
        getGroupTransitionProps,
        status,
    } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref })
    const item = useTreeItemModel(itemId)

    return (
        <TreeItemProvider {...getContextProviderProps()}>
            <TreeItemRoot {...getRootProps(other)}>
                <TreeItemContent {...getContentProps()} sx={{ borderRadius: '8px' }}>
                    <TreeItemIconContainer {...getIconContainerProps()}>
                        <TreeItemIcon status={status} slotProps={{ icon: { sx: { fontSize: '48px' } } }}/>
                    </TreeItemIconContainer>
                    <CustomLabel
                        {...getLabelProps({ item, actions })}
                    />
                </TreeItemContent>
                {children && <TreeItemGroupTransition {...getGroupTransitionProps()}
                    sx={{
                        marginLeft: '15px',
                        paddingLeft: '8px',
                        borderLeft: `1px dashed ${alpha('#000000', 0.3)}`,
                    }}
                />}
            </TreeItemRoot>
        </TreeItemProvider>
    )
})

const addDepth = (item, depth = 0) => {
    return {
        id: item.id,
        label: item.label,
        description: item.description,
        pluggedInFrontend: item.pluggedInFrontend,
        typeFeature: item.typeFeature,
        depth,
        children: item.subFeatures?.map(it => addDepth(it, depth + 1)),
    }
}

const AdminFeatureExplorer = ({
    adminFeature,
    actions = [],
}) => {
    const [expandedItems, setExpandedItems] = useState([])

    const items = useMemo(() => {
        const list = isUndefined(adminFeature) ? [] : [adminFeature]
        return list.map(item => addDepth(item, 0))
    }, [adminFeature])

    const filteredActions = actions.filter(a => a.displayed || isUndefined(a.displayed))

    return (
        <Card>
            <CardContent>
                <RichTreeView
                    items={items}
                    expandedItems={expandedItems}
                    onExpandedItemsChange={(_, ids) => setExpandedItems(ids)}
                    disableSelection
                    slots={{ item: CustomTreeItem }}
                    slotProps={{
                        item: {
                            actions: filteredActions,
                        },
                    }}
                    itemChildrenIndentation={0}
                />
            </CardContent>
        </Card>
    )
}

AdminFeatureExplorer.propTypes = {
    adminFeature: PropTypes.shape({}),
    actions: PropTypes.arrayOf(PropTypes.shape({})),
}

export default AdminFeatureExplorer